import { BlockService, ContentBlock } from './blockService';

export interface BlockContainer {
  id: string;
  blocks: string[]; // Array of block IDs
}


// Unified block interface that serves both UI and backend needs
export interface UnifiedBlock extends ContentBlock {
  // UI-specific fields
  type: 'paragraph' | 'heading' | 'list-item' | 'habit-link' | 'note-link' | 'image';
  level?: number; // For headings (1-6) or list indentation
  
  // Computed fields for compatibility
  content: string; // Alias for text_value
  syncStatus?: 'synced' | 'syncing' | 'error';
}

export interface BlockManagerCallbacks {
  onBlocksChanged?: (blocks: UnifiedBlock[]) => void;
  onContainerUpdated?: (container: BlockContainer) => Promise<void>;
  onSyncStatusChanged?: (status: 'synced' | 'syncing' | 'error') => void;
}

export interface BlockManagerConfig {
  userId: number;
  containerType: 'task' | 'note' | 'dailyNote' | 'block';
  containerId?: string;
  callbacks?: BlockManagerCallbacks;
}

/**
 * Centralized block management for consistent behavior across components
 */
export class BlockManager {
  private userId: number;
  private containerType: string;
  private containerId?: string;
  private callbacks: BlockManagerCallbacks;
  private contentBlocks: UnifiedBlock[] = [];
  private pendingOperations = new Set<string>();
  private syncQueue: (() => Promise<any>)[] = [];
  private isSyncing = false;

  constructor(config: BlockManagerConfig) {
    this.userId = config.userId;
    this.containerType = config.containerType;
    this.containerId = config.containerId;
    this.callbacks = config.callbacks || {};
  }

  /**
   * Get current content blocks
   */
  getContentBlocks(): UnifiedBlock[] {
    // Add safety filter to ensure all blocks have required properties
    const validBlocks = this.contentBlocks.filter(block => 
      block && 
      block.block_id && 
      typeof block.block_id === 'string'
    );
    return [...validBlocks];
  }

  /**
   * Get block by ID
   */
  getBlockById(blockId: string): UnifiedBlock | undefined {
    return this.contentBlocks.find(block => block.block_id === blockId);
  }

  /**
   * Get current container ID
   */
  getContainerId(): string | undefined {
    return this.containerId;
  }

  /**
   * Set content blocks (for initial load)
   */
  setContentBlocks(blocks: ContentBlock[]): void {
    // Convert ContentBlocks to UnifiedBlocks, filtering out invalid ones
    const validBlocks = blocks.filter(block => 
      block && 
      typeof block.block_id === 'string' && 
      block.block_id.length > 0
    );
    this.contentBlocks = validBlocks.map(block => this.contentBlockToUnified(block));
    
    // Notify via callback
    if (this.callbacks.onBlocksChanged) {
      this.callbacks.onBlocksChanged(this.getContentBlocks());
    }
  }

  /**
   * Add an operation to the sync queue to ensure sequential execution.
   */
  private addToSyncQueue<T>(operation: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.syncQueue.push(() => operation().then(resolve).catch(reject));
      this.processSyncQueue();
    });
  }

  private async processSyncQueue() {
    if (this.isSyncing || this.syncQueue.length === 0) {
      return;
    }
    this.isSyncing = true;
    const operation = this.syncQueue.shift();
    if (operation) {
      try {
        await operation();
      } catch (error) {
        console.error("Error processing sync queue operation:", error);
      } finally {
        this.isSyncing = false;
        this.processSyncQueue();
      }
    } else {
      this.isSyncing = false;
    }
  }

  /**
   * Convert ContentBlock to UnifiedBlock
   */
  private contentBlockToUnified(contentBlock: ContentBlock): UnifiedBlock {
    // First, get the type from content_type field
    let type = this.mapContentTypeToUnifiedType(contentBlock.content_type);
    let level: number | undefined;
    let content = contentBlock.text_value;
    
    // For blocks that may have formatting in their content, parse it
    if (type === 'paragraph') {
      // Parse content for headings and list items that might be stored as 'note' type
      const parsed = this.parseBlockContent(contentBlock.text_value);
      type = parsed.type;
      level = parsed.level;
      content = parsed.content;
    } else if (type === 'heading') {
      // For heading type blocks, still parse to get the level
      const headingMatch = content.match(/^(#{1,6})\s+(.*)$/);
      if (headingMatch) {
        level = headingMatch[1].length;
        content = headingMatch[2];
      }
    } else if (type === 'list-item') {
      // For list item type blocks, remove the bullet
      const listMatch = content.match(/^-\s+(.*)$/);
      if (listMatch) {
        content = listMatch[1];
      }
    }
    
    return {
      ...contentBlock,
      type,
      level,
      content: content // Use the parsed/cleaned content
    };
  }

  /**
   * Parse block content to determine type and level
   */
  private parseBlockContent(textValue: string): { type: UnifiedBlock['type'], level?: number, content: string } {
    const trimmed = textValue;
    
    // Check for headings
    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      return {
        type: 'heading',
        level: headingMatch[1].length,
        content: headingMatch[2]
      };
    }
    
    // Check for list items
    const listMatch = trimmed.match(/^[-*+]\s+(.*)$/);
    if (listMatch) {
      return {
        type: 'list-item',
        content: listMatch[1]
      };
    }
    
    // Default to paragraph
    return {
      type: 'paragraph',
      content: trimmed
    };
  }

  /**
   * Load content blocks by IDs
   */
  async loadContentBlocks(blockIds: string[]): Promise<UnifiedBlock[]> {
    if (blockIds.length === 0) {
      this.contentBlocks = [];
      if (this.callbacks.onBlocksChanged) {
        this.callbacks.onBlocksChanged(this.getContentBlocks());
      }
      return [];
    }

    try {
      const blocks = await Promise.all(
        blockIds.map(async (blockId) => {
          try {
            const block = await BlockService.getBlockById(blockId);
            return block;
          } catch (error) {
            console.error(`❌ Error loading block ${blockId}:`, error);
            return null;
          }
        })
      );

      // Filter out failed block loads and ensure all blocks have required properties
      const validBlocks = blocks.filter((block): block is ContentBlock => 
        block !== null && 
        block !== undefined && 
        typeof block.block_id === 'string' &&
        block.block_id.length > 0
      );
      // TODO: sort by order instead of creation date
      // validBlocks.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      console.log('📋 BlockManager: loadContentBlocks loaded', validBlocks.length, 'blocks for IDs:', blockIds);
      this.setContentBlocks(validBlocks);
      return this.getContentBlocks();
    } catch (error) {
      console.error('❌ Error in loadContentBlocks:', error);
      // Clear blocks and notify on error
      this.contentBlocks = [];
      if (this.callbacks.onBlocksChanged) {
        this.callbacks.onBlocksChanged(this.getContentBlocks());
      }
      return [];
    }
  }

  /**
   * Create a new content block with frontend-provided ID
   */
  private async createContentBlockWithId(blockId: string, textValue: string): Promise<ContentBlock | null> {
    try {
      const safeTextValue = textValue ?? '';
      return await BlockService.createNoteBlockWithId(this.userId, blockId, safeTextValue);
    } catch (error) {
      console.error('Error creating content block with ID:', error);
      return null;
    }
  }

  /**
   * Map UnifiedBlock type to ContentBlock content_type
   */
  private mapUnifiedTypeToContentType(unifiedType: UnifiedBlock['type']): string {
    switch (unifiedType) {
      case 'paragraph':
        return 'note';
      case 'heading':
        return 'heading';
      case 'list-item':
        return 'list-item';
      case 'habit-link':
        return 'habit-link';
      case 'note-link':
        return 'note-link';
      case 'image':
        return 'image';
      default:
        return 'note';
    }
  }

  /**
   * Map ContentBlock content_type to UnifiedBlock type
   */
  private mapContentTypeToUnifiedType(contentType: string): UnifiedBlock['type'] {
    switch (contentType) {
      case 'note':
        return 'paragraph';
      case 'heading':
        return 'heading';
      case 'list-item':
        return 'list-item';
      case 'habit-link':
        return 'habit-link';
      case 'note-link':
        return 'note-link';
      case 'image':
        return 'image';
      default:
        return 'paragraph';
    }
  }

  /**
   * Update an existing content block
   */
  private async updateContentBlock(blockId: string, textValue: string, contentType?: string): Promise<ContentBlock | null> {
    try {
      const safeTextValue = textValue ?? '';
      
      // Get the current block's type if not provided
      let actualContentType = contentType;
      if (!actualContentType) {
        const existingBlock = this.contentBlocks.find(block => block.block_id === blockId);
        actualContentType = this.mapUnifiedTypeToContentType(existingBlock?.type || 'paragraph');
      }
      
      return await BlockService.updateBlock(blockId, {
        content_type: actualContentType,
        text_value: safeTextValue
      });
    } catch (error) {
      console.error('Error updating content block:', error);
      return null;
    }
  }

  /**
   * Handle new block creation (called when user creates a new block)
   * @param blockId - The ID of the new block
   * @param content - The content of the new block
   * @param afterBlockId - Optional: Insert after this block ID. If not provided, adds to end.
   */
  async handleBlockCreate(blockId: string, content: string, afterBlockId?: string): Promise<void> {
    // Prevent concurrent creations of the same block
    if (this.pendingOperations.has(`create-${blockId}`)) {
      return;
    }

    // Check if block already exists to prevent duplicates
    const existingBlock = this.contentBlocks.find(block => block.block_id === blockId);
    if (existingBlock) {
      return;
    }

    try {
      this.pendingOperations.add(`create-${blockId}`);
      
      // Notify sync status change
      this.callbacks.onSyncStatusChanged?.('syncing');
      
      const newBlock = await this.createContentBlockWithId(blockId, content);
      if (newBlock) {
        // Validate that we got a proper block_id
        if (!newBlock.block_id || typeof newBlock.block_id !== 'string') {
          throw new Error('Invalid block_id returned from block creation');
        }
        
        // Add to local state at the correct position
        const alreadyExists = this.contentBlocks.some(block => block.block_id === blockId);
        if (!alreadyExists) {
          const unifiedBlock = this.contentBlockToUnified(newBlock);
          
          if (afterBlockId) {
            // Find the position of the block we want to insert after
            const afterBlockIndex = this.contentBlocks.findIndex(block => block.block_id === afterBlockId);
            
            if (afterBlockIndex >= 0) {
              // Insert right after the specified block
              this.contentBlocks.splice(afterBlockIndex + 1, 0, unifiedBlock);
            } else {
              // Fallback: add to end if afterBlock not found
              this.contentBlocks.push(unifiedBlock);
            }
          } else {
            // Default behavior: add to end
            this.contentBlocks.push(unifiedBlock);
          }
          
          // Notify via callbacks
          this.callbacks.onBlocksChanged?.(this.getContentBlocks());
        }
        
        this.callbacks.onSyncStatusChanged?.('synced');
        
        // Update container if callback provided
        await this.updateContainer();
      }
    } catch (error) {
      console.error('❌ Failed to create block:', error);
      
      // Notify error status
      this.callbacks.onSyncStatusChanged?.('error');
    } finally {
      this.pendingOperations.delete(`create-${blockId}`);
    }
  }

  /**
   * Handle block deletion (called when blocks are merged/deleted)
   */
  async handleBlockDelete(blockId: string): Promise<void> {
    if (this.pendingOperations.has(`delete-${blockId}`)) {
      return;
    }

    try {
      this.pendingOperations.add(`delete-${blockId}`);
      this.callbacks.onSyncStatusChanged?.('syncing');
      
      // Validate block_id format
      if (!blockId || typeof blockId !== 'string') {
        this.callbacks.onSyncStatusChanged?.('error');
        return;
      }
      
      // Remove from backend
      await BlockService.deleteBlock(blockId);
      
      // Remove from local state immediately
      this.contentBlocks = this.contentBlocks.filter(block => block.block_id !== blockId);
      this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      this.callbacks.onSyncStatusChanged?.('synced');
      
      // Update container if callback provided
      await this.updateContainer();
    } catch (error) {
      console.error('❌ Failed to delete block:', error);
      this.callbacks.onSyncStatusChanged?.('error');
    } finally {
      this.pendingOperations.delete(`delete-${blockId}`);
    }
  }

  /**
   * Create a new block.
   * @param content The initial content of the block.
   * @param afterBlockId The ID of the block after which the new block should be inserted.
   * @returns The ID of the newly created block, or null if creation fails.
   */
  async createBlock(content: string, afterBlockId?: string): Promise<string | null> {
    return this.addToSyncQueue(async () => {
      const newBlockId = this.generateUniqueBlockId();

      const tempBlock: UnifiedBlock = {
        id: Date.now(), // Temporary unique ID for React key
        block_id: newBlockId,
        user_id: this.userId,
        content_type: 'note',
        text_value: content,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: this.parseBlockContent(content).type,
        level: this.parseBlockContent(content).level,
        content: this.parseBlockContent(content).content,
        syncStatus: 'syncing',
      };

      const insertIndex = afterBlockId
        ? this.contentBlocks.findIndex(b => b.block_id === afterBlockId) + 1
        : this.contentBlocks.length;
      
      this.contentBlocks.splice(insertIndex, 0, tempBlock);
      this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      this.callbacks.onSyncStatusChanged?.('syncing');

      try {
        const createdBackendBlock = await this.createContentBlockWithId(newBlockId, content);
        if (createdBackendBlock) {
          const blockIndex = this.contentBlocks.findIndex(b => b.block_id === newBlockId);
          if (blockIndex !== -1) {
            this.contentBlocks[blockIndex] = this.contentBlockToUnified(createdBackendBlock);
            this.contentBlocks[blockIndex].syncStatus = 'synced';
            this.callbacks.onSyncStatusChanged?.('synced');
            await this.updateContainer();
          }
        } else {
          throw new Error("Backend create operation did not return a block.");
        }
        return newBlockId;
      } catch (error) {
        console.error('❌ Failed to create block:', error);
        const blockIndex = this.contentBlocks.findIndex(b => b.block_id === newBlockId);
        if (blockIndex !== -1) {
          this.contentBlocks.splice(blockIndex, 1);
        }
        this.callbacks.onSyncStatusChanged?.('error');
        return null;
      } finally {
        this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      }
    });
  }

  /**
   * Delete a block.
   * @param blockId The ID of the block to delete.
   */
  async deleteBlock(blockId: string): Promise<void> {
    return this.addToSyncQueue(async () => {
      const blockIndex = this.contentBlocks.findIndex(b => b.block_id === blockId);
      if (blockIndex === -1) return;

      const blockToDelete = this.contentBlocks[blockIndex];
      
      // Optimistically remove from local state
      this.contentBlocks.splice(blockIndex, 1);
      this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      this.callbacks.onSyncStatusChanged?.('syncing');

      try {
        await BlockService.deleteBlock(blockId);
        this.callbacks.onSyncStatusChanged?.('synced');
        await this.updateContainer();
      } catch (error) {
        console.error('❌ Failed to delete block:', error);
        // Revert on failure
        this.contentBlocks.splice(blockIndex, 0, { ...blockToDelete, syncStatus: 'error' });
        this.callbacks.onBlocksChanged?.(this.getContentBlocks());
        this.callbacks.onSyncStatusChanged?.('error');
      }
    });
  }

  /**
   * Update the container with current block IDs
   */
  private async updateContainer(): Promise<void> {
    if (this.callbacks.onContainerUpdated && this.containerId) {
      const container: BlockContainer = {
        id: this.containerId,
        blocks: this.contentBlocks.map(block => block.block_id)
      };
      
      try {
        await this.callbacks.onContainerUpdated(container);
      } catch (error) {
        console.error('❌ Failed to update container:', error);
      }
    }
  }

  /**
   * Convert blocks to markdown content
   */
  blocksToMarkdown(): string {
    return this.contentBlocks.map(block => {
      switch (block.type) {
        case 'heading':
          const hashes = '#'.repeat(block.level || 1);
          return block.content ? `${hashes} ${block.content}` : '';
        case 'list-item':
          return block.content ? `- ${block.content}` : '';
        default:
          return block.content || block.text_value;
      }
    }).join('\n');
  }

  /**
   * Update block content
   */
  async updateBlockContent(blockId: string, content: string): Promise<void> {
    return this.addToSyncQueue(async () => {
      const blockIndex = this.contentBlocks.findIndex(block => block.block_id === blockId);
      if (blockIndex === -1) {
        console.error('❌ Block not found for content update:', blockId);
        return;
      }

      const originalBlock = { ...this.contentBlocks[blockIndex] };
      const parsed = this.parseBlockContent(content);

      // Optimistically update local state
      this.contentBlocks[blockIndex] = {
        ...originalBlock,
        content: parsed.content,
        text_value: content, // Store raw content in text_value
        type: parsed.type,
        level: parsed.level,
        syncStatus: 'syncing',
      };
      this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      this.callbacks.onSyncStatusChanged?.('syncing');

      try {
        // Use `content` which includes markdown, not `parsed.content`
        const updatedBackendBlock = await this.updateContentBlock(blockId, content);
        if (updatedBackendBlock) {
          this.contentBlocks[blockIndex] = this.contentBlockToUnified(updatedBackendBlock);
          this.contentBlocks[blockIndex].syncStatus = 'synced';
          this.callbacks.onSyncStatusChanged?.('synced');
        } else {
          throw new Error('Backend update did not return a block.');
        }
      } catch (error) {
        console.error(`❌ Failed to save block ${blockId}:`, error);
        // Revert on failure
        this.contentBlocks[blockIndex] = { ...originalBlock, syncStatus: 'error' };
        this.callbacks.onSyncStatusChanged?.('error');
      } finally {
        this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      }
    });
  }

  /**
   * Update block type and level
   */
  async updateBlockType(blockId: string, type: UnifiedBlock['type'], level?: number): Promise<void> {
    this.addToSyncQueue(async () => {
      const blockIndex = this.contentBlocks.findIndex(block => block.block_id === blockId);
      if (blockIndex === -1) {
        console.error('❌ Block not found for type update:', blockId);
        return;
      }

      const originalBlock = { ...this.contentBlocks[blockIndex] };
      
      // Optimistically update local state
      this.contentBlocks[blockIndex] = {
        ...originalBlock,
        type,
        level,
        syncStatus: 'syncing',
      };
      this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      this.callbacks.onSyncStatusChanged?.('syncing');

      try {
        const formattedContent = this.formatBlockContent(originalBlock.content, type, level);
        const contentType = this.mapUnifiedTypeToContentType(type);
        const updatedBackendBlock = await this.updateContentBlock(blockId, formattedContent, contentType);
        
        if (updatedBackendBlock) {
          this.contentBlocks[blockIndex] = this.contentBlockToUnified(updatedBackendBlock);
          this.contentBlocks[blockIndex].syncStatus = 'synced';
          this.callbacks.onSyncStatusChanged?.('synced');
        } else {
          throw new Error('Backend update did not return a block for type change.');
        }
      } catch (error) {
        console.error('❌ Failed to update block type:', error);
        this.contentBlocks[blockIndex] = { ...originalBlock, syncStatus: 'error' };
        this.callbacks.onSyncStatusChanged?.('error');
      } finally {
        this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      }
    });
  }

  /**
   * Update both block type and content in a single API call
   */
  async updateBlockTypeAndContent(blockId: string, type: UnifiedBlock['type'], content: string, level?: number): Promise<void> {
    this.addToSyncQueue(async () => {
      const blockIndex = this.contentBlocks.findIndex(block => block.block_id === blockId);
      if (blockIndex === -1) {
        console.error('❌ Block not found for type and content update:', blockId);
        return;
      }

      const originalBlock = { ...this.contentBlocks[blockIndex] };

      // Update local state immediately
      this.contentBlocks[blockIndex] = {
        ...originalBlock,
        type,
        level,
        content,
        syncStatus: 'syncing'
      };
      this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      this.callbacks.onSyncStatusChanged?.('syncing');

      try {
        const formattedContent = this.formatBlockContent(content, type, level);
        const contentType = this.mapUnifiedTypeToContentType(type);
        const updatedBackendBlock = await this.updateContentBlock(blockId, formattedContent, contentType);

        if (updatedBackendBlock) {
          // Update local state with the response from backend
          this.contentBlocks[blockIndex] = this.contentBlockToUnified(updatedBackendBlock);
          this.contentBlocks[blockIndex].syncStatus = 'synced';
          this.callbacks.onSyncStatusChanged?.('synced');
        } else {
          throw new Error('Backend update did not return a block for type/content change.');
        }
      } catch (error) {
        console.error('❌ Failed to update block type and content:', error);
        this.contentBlocks[blockIndex] = { ...originalBlock, syncStatus: 'error' };
        this.callbacks.onSyncStatusChanged?.('error');
      } finally {
        this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      }
    });
  }

  /**
   * Updates block properties (metadata)
   */
  async updateBlockProperties(blockId: string, properties: Record<string, any>): Promise<void> {
    this.addToSyncQueue(async () => {
      const blockIndex = this.contentBlocks.findIndex(block => block.block_id === blockId);
      if (blockIndex === -1) {
        console.error('❌ Block not found for properties update:', blockId);
        return;
      }

      const originalBlock = { ...this.contentBlocks[blockIndex] };

      // Update local state immediately
      this.contentBlocks[blockIndex] = {
        ...originalBlock,
        properties: { ...originalBlock.properties, ...properties },
        syncStatus: 'syncing'
      };
      this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      this.callbacks.onSyncStatusChanged?.('syncing');

      try {
        const updatedBackendBlock = await BlockService.updateBlock(blockId, {
          content_type: this.mapUnifiedTypeToContentType(originalBlock.type),
          text_value: originalBlock.content,
          properties: { ...originalBlock.properties, ...properties }
        });

        if (updatedBackendBlock) {
          // Update local state with the response from backend
          this.contentBlocks[blockIndex] = this.contentBlockToUnified(updatedBackendBlock);
          this.callbacks.onBlocksChanged?.(this.getContentBlocks());
          this.callbacks.onSyncStatusChanged?.('synced');
        } else {
          // Revert on failure
          this.contentBlocks[blockIndex] = { ...originalBlock, syncStatus: 'error' };
          this.callbacks.onBlocksChanged?.(this.getContentBlocks());
          this.callbacks.onSyncStatusChanged?.('error');
        }
      } catch (error) {
        console.error('❌ Failed to update block properties:', error);
        // Revert on error
        this.contentBlocks[blockIndex] = { ...originalBlock, syncStatus: 'error' };
        this.callbacks.onBlocksChanged?.(this.getContentBlocks());
        this.callbacks.onSyncStatusChanged?.('error');
      }
    });
  }

  /**
   * Format block content based on type and level
   */
  private formatBlockContent(content: string, type: UnifiedBlock['type'], level?: number): string {
    switch (type) {
      case 'heading':
        const hashes = '#'.repeat(level || 1);
        return content ? `${hashes} ${content}` : '';
      case 'list-item':
        return content ? `- ${content}` : '';
      case 'habit-link':
        // For habit-link blocks, content is already JSON, so return as-is
        return content;
      case 'note-link':
        // For note-link blocks, content is already JSON, so return as-is
        return content;
      case 'image':
        // For image blocks, content is the image URL, so return as-is
        return content;
      default:
        return content;
    }
  }

  /**
   * Get block IDs for external use
   */
  getBlockIds(): string[] {
    return this.contentBlocks.map(block => block.block_id);
  }

  /**
   * Update container ID (useful for dynamic containers)
   * Clears existing blocks when container changes
   */
  setContainerId(containerId: string): void {
    const previousContainerId = this.containerId;
    this.containerId = containerId;
    
    console.log('🔄 BlockManager: setContainerId from', previousContainerId, 'to', containerId);
    
    // Clear blocks when switching to a different container
    if (previousContainerId !== containerId) {
      console.log('🗑️ BlockManager: clearing blocks due to container change');
      this.contentBlocks = [];
      if (this.callbacks.onBlocksChanged) {
        this.callbacks.onBlocksChanged(this.getContentBlocks());
      }
    }
  }

  /**
   * Split a block at the given cursor position
   */
  async splitBlock(blockId: string, cursorPosition: number): Promise<string | null> {
    const blockIndex = this.contentBlocks.findIndex(b => b.block_id === blockId);
    if (blockIndex === -1) {
      console.error('❌ Block not found for splitting:', blockId);
      return null;
    }

    const originalBlock = this.contentBlocks[blockIndex];
    const textBeforeCursor = originalBlock.content.substring(0, cursorPosition);
    const textAfterCursor = originalBlock.content.substring(cursorPosition);
    
    // This operation involves two separate API calls. We'll wrap them in the queue.
    return this.addToSyncQueue(async () => {
      // 1. Optimistically update the current block and create the new one
      const newBlockId = this.generateUniqueBlockId();
      const tempNewBlock: UnifiedBlock = {
        id: Date.now(),
        block_id: newBlockId,
        user_id: this.userId,
        content_type: originalBlock.content_type,
        text_value: textAfterCursor,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        type: originalBlock.type,
        level: originalBlock.level,
        content: textAfterCursor,
        syncStatus: 'syncing',
      };

      this.contentBlocks[blockIndex] = { ...originalBlock, content: textBeforeCursor, text_value: textBeforeCursor, syncStatus: 'syncing' };
      this.contentBlocks.splice(blockIndex + 1, 0, tempNewBlock);
      this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      this.callbacks.onSyncStatusChanged?.('syncing');

      try {
        // 2. Perform API calls
        const updatePromise = this.updateContentBlock(blockId, textBeforeCursor);
        const createPromise = this.createContentBlockWithId(newBlockId, textAfterCursor);
        const [updatedBackendBlock, createdBackendBlock] = await Promise.all([updatePromise, createPromise]);

        if (updatedBackendBlock && createdBackendBlock) {
          // 3. Update local state with final versions
          const updatedIndex = this.contentBlocks.findIndex(b => b.block_id === blockId);
          if (updatedIndex !== -1) {
            this.contentBlocks[updatedIndex] = this.contentBlockToUnified(updatedBackendBlock);
            this.contentBlocks[updatedIndex].syncStatus = 'synced';
          }
          const newIndex = this.contentBlocks.findIndex(b => b.block_id === newBlockId);
          if (newIndex !== -1) {
            this.contentBlocks[newIndex] = this.contentBlockToUnified(createdBackendBlock);
            this.contentBlocks[newIndex].syncStatus = 'synced';
          }
          this.callbacks.onSyncStatusChanged?.('synced');
          await this.updateContainer();
        } else {
          throw new Error('One or both split operations failed.');
        }
        return newBlockId;
      } catch (error) {
        console.error('❌ Failed to split block:', error);
        // 4. Revert on failure
        this.contentBlocks.splice(blockIndex + 1, 1); // Remove new block
        this.contentBlocks[blockIndex] = { ...originalBlock, syncStatus: 'error' }; // Restore original
        this.callbacks.onSyncStatusChanged?.('error');
        return null;
      } finally {
        this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      }
    });
  }

  /**
   * Merge current block with previous block
   */
  async mergeBlocks(currentBlockId: string, previousBlockId: string): Promise<boolean> {
    const currentIndex = this.contentBlocks.findIndex(b => b.block_id === currentBlockId);
    const previousIndex = this.contentBlocks.findIndex(b => b.block_id === previousBlockId);

    if (currentIndex === -1 || previousIndex === -1) {
      console.error('❌ Blocks not found for merging:', { currentBlockId, previousBlockId });
      return false;
    }

    const currentBlock = this.contentBlocks[currentIndex];
    const previousBlock = this.contentBlocks[previousIndex];
    const mergedContent = previousBlock.content + currentBlock.content;

    return this.addToSyncQueue(async () => {
      // 1. Optimistically update state
      this.contentBlocks.splice(currentIndex, 1);
      this.contentBlocks[previousIndex] = { ...previousBlock, content: mergedContent, text_value: mergedContent, syncStatus: 'syncing' };
      this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      this.callbacks.onSyncStatusChanged?.('syncing');

      try {
        // 2. Perform API calls
        const updatePromise = this.updateContentBlock(previousBlockId, mergedContent);
        const deletePromise = BlockService.deleteBlock(currentBlockId);
        const [updatedBackendBlock] = await Promise.all([updatePromise, deletePromise]);

        if (updatedBackendBlock) {
          // 3. Update local state with final version
          const updatedIndex = this.contentBlocks.findIndex(b => b.block_id === previousBlockId);
          if (updatedIndex !== -1) {
            this.contentBlocks[updatedIndex] = this.contentBlockToUnified(updatedBackendBlock);
            this.contentBlocks[updatedIndex].syncStatus = 'synced';
          }
          this.callbacks.onSyncStatusChanged?.('synced');
          await this.updateContainer();
        } else {
          throw new Error('Merge operation failed.');
        }
        return true;
      } catch (error) {
        console.error('❌ Failed to merge blocks:', error);
        // 4. Revert on failure
        this.contentBlocks.splice(currentIndex, 0, { ...currentBlock, syncStatus: 'error' });
        this.contentBlocks[previousIndex] = { ...previousBlock, syncStatus: 'error' };
        this.callbacks.onSyncStatusChanged?.('error');
        return false;
      } finally {
        this.callbacks.onBlocksChanged?.(this.getContentBlocks());
      }
    });
  }

  /**
   * Get navigation info for a block
   */
  getNavigationInfo(blockId: string): {
    index: number;
    isFirst: boolean;
    isLast: boolean;
    previousBlockId?: string;
    nextBlockId?: string;
  } {
    const index = this.contentBlocks.findIndex(block => block.block_id === blockId);
    
    if (index === -1) {
      return {
        index: -1,
        isFirst: false,
        isLast: false,
        previousBlockId: undefined,
        nextBlockId: undefined
      };
    }
    
    return {
      index,
      isFirst: index === 0,
      isLast: index === this.contentBlocks.length - 1,
      previousBlockId: index > 0 ? this.contentBlocks[index - 1].block_id : undefined,
      nextBlockId: index < this.contentBlocks.length - 1 ? this.contentBlocks[index + 1].block_id : undefined
    };
  }

  /**
   * Create an initial empty block for when no blocks exist
   */
  async createInitialBlock(): Promise<string | null> {
    if (!this.containerId) {
      console.warn('Cannot create initial block without container ID');
      return null;
    }
    
    if (this.contentBlocks.length > 0) {
      return this.contentBlocks[0].block_id;
    }

    // Since this is the first block, we can call createBlock directly
    return this.createBlock('');
  }

  /**
   * Generate a unique block ID that doesn't conflict with existing blocks
   */
  private generateUniqueBlockId(): string {
    let attempts = 0;
    const maxAttempts = 100;
    
    while (attempts < maxAttempts) {
      const id = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      // Check if this ID already exists
      const exists = this.contentBlocks.some(block => block.block_id === id);
      if (!exists) {
        return id;
      }
      
      attempts++;
      // Small delay to ensure different timestamp
      if (attempts > 1) {
        // Use a more unique approach for subsequent attempts
        const timestamp = Date.now() + attempts;
        const randomPart = Math.random().toString(36).substr(2, 12);
        const id = `block-${timestamp}-${randomPart}`;
        
        const exists = this.contentBlocks.some(block => block.block_id === id);
        if (!exists) {
          return id;
        }
      }
    }
    
    // Fallback - this should never happen
    return `block-${Date.now()}-${Math.random().toString(36).substr(2, 15)}-fallback`;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.contentBlocks = [];
    this.pendingOperations.clear();
    this.syncQueue = [];
    this.isSyncing = false;
  }
}

export default BlockManager;
